function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { HttpClient } from 'api/http-client';
import { TaskSourceType } from 'api/tasks';
import { getDocumentName } from 'components/organisms/ck-collab-editor/get-document-name';
import { isCKWsCloud } from 'config/environment';
import { EditorModes } from 'data/documents/constants';
import { logger } from 'services';
import { updateTaskAssigneeAndResponsibleCompany, updateTasksAssigneeAndResponsibleCompany } from 'utils/strategic-partner-utils';
const DOCUMENTS_API = isCKWsCloud() ? '/cloud-collaborate' : '/collaborate';
class DocumentsHttpClient extends HttpClient {
    async getDocumentsList(projectID) {
        return this.get(`/projects/${projectID}/documents`);
    }
    async getDocument(projectID, documentID) {
        return this.get(`/projects/${projectID}/documents/${documentID}`);
    }
    // three endpoints here are meant for document versioning without real-time collaboration. To be integrated with revisionsAdapter if we implement later
    async getAllDocumentVersions(documentID, mode) {
        return this.get(`/documents/${documentID}/get-versions/${mode}`);
    }
    async addDocumentVersion(documentID, payload) {
        return this.post(`/documents/${documentID}/add-version`, payload);
    }
    async getDocumentVersion(documentID, versionId) {
        return this.get(`/documents/${documentID}/version/${versionId}`);
    }
    async getTasks(projectID, documentID) {
        return this.get(`/projects/${projectID}/documents/${documentID}/tasks`).then((tasks)=>updateTasksAssigneeAndResponsibleCompany(tasks));
    }
    async createTask(projectID, documentID, data, meta) {
        const initSource = {
            sourceType: TaskSourceType.Document,
            sourceId: documentID
        };
        let finalSource;
        if (meta.resourceSectionId) {
            finalSource = {
                ...initSource,
                metaData: {
                    resourceSectionId: meta.resourceSectionId
                }
            };
        } else if (meta.docRefText) {
            finalSource = {
                ...initSource,
                metaData: {
                    taskFromDoc: meta.docRefText
                }
            };
        } else {
            finalSource = initSource;
        }
        const payload = {
            ...data,
            source: finalSource
        };
        return this.post(`/projects/${projectID}/tasks`, payload).then((task)=>updateTaskAssigneeAndResponsibleCompany(task));
    }
    async createSectionTask(projectID, data, meta) {
        const source = {
            sourceType: TaskSourceType.DocumentSection,
            sourceId: meta.documentSectionId,
            metaData: {
                resourceSectionId: meta.resourceSectionId
            }
        };
        const payload = {
            ...data,
            private: meta.private,
            source
        };
        return this.post(`/projects/${projectID}/tasks`, payload).then((task)=>updateTaskAssigneeAndResponsibleCompany(task));
    }
    async addExistingSectionTask(projectID, taskList, meta) {
        const source = {
            sourceType: TaskSourceType.DocumentSection,
            sourceId: meta.documentSectionId,
            metaData: {
                resourceSectionId: meta.resourceSectionId
            }
        };
        const tasksWithSection = taskList.map((task)=>({
                taskName: task.taskName,
                taskId: task.taskId,
                source
            }));
        return this.post(`/projects/${projectID}/add-sources`, tasksWithSection);
    }
    async deleteExistingSectionTask(projectID, task) {
        return this.put(`/projects/${projectID}/delete-sources`, task);
    }
    async updateDocument(projectID, documentID, sectionid, versionId, data) {
        const payload = {
            versionId,
            document: {
                sections: [
                    {
                        documentSectionId: sectionid,
                        content: data
                    }
                ]
            }
        };
        return this.put(`/projects/${projectID}/documents/${documentID}/`, payload);
    }
    async updateStatusUpdateDocument(statusUpdateId, documentID, sectionid, versionId, data) {
        const payload = {
            versionId,
            document: {
                sections: [
                    {
                        documentSectionId: sectionid,
                        content: data
                    }
                ]
            }
        };
        return this.put(`/status-updates/${statusUpdateId}/document/${documentID}`, payload);
    }
    async createPrivateSection(projectID, documentID, data) {
        const payload = {
            document: {
                sections: [
                    {
                        meta: {
                            sectionType: EditorModes.Private
                        },
                        content: data
                    }
                ]
            }
        };
        return this.put(`/projects/${projectID}/documents/${documentID}/`, payload);
    }
    async createStatusUpdatePrivateSection(statusUpdateId, documentID, data) {
        const payload = {
            document: {
                sections: [
                    {
                        meta: {
                            sectionType: EditorModes.Private
                        },
                        content: data
                    }
                ]
            }
        };
        return this.put(`/status-updates/${statusUpdateId}/document/${documentID}`, payload);
    }
    async updateDocumentTaskSectionsMeta(projectID, documentID, section, map) {
        // TODO: Now the content is a mandatory field. That
        // is not optimal. Remove that rule
        const payload = {
            document: {
                sections: [
                    {
                        documentSectionId: section.id,
                        meta: {
                            sectionType: section.type,
                            resourceSections: map
                        }
                    }
                ]
            }
        };
        return this.put(`/projects/${projectID}/documents/${documentID}`, payload);
    }
    async updateDocumentTitle(projectID, documentID, title) {
        const payload = {
            document: {
                title
            }
        };
        return this.put(`/projects/${projectID}/documents/${documentID}/`, payload);
    }
    async updateDocumentFreeze(projectID, documentID, frozen) {
        const payload = {
            document: {
                frozen
            }
        };
        return this.put(`/projects/${projectID}/documents/${documentID}/`, payload);
    }
    async updateDocumentResources({ projectId, documentId, documentSectionId, sectionType, resourceSections }) {
        const payload = {
            document: {
                sections: [
                    {
                        documentSectionId,
                        meta: {
                            sectionType,
                            resourceSections
                        }
                    }
                ]
            }
        };
        return this.put(`/projects/${projectId}/documents/${documentId}/`, payload);
    }
    constructor(...args){
        super(...args), _define_property(this, "flushCollaborationSession", async (projectId, docId)=>{
            const privateDocName = getDocumentName(docId, EditorModes.Private);
            const publicDocName = getDocumentName(docId, EditorModes.Public);
            const privateDocument = this.delete(`/projects/${projectId[1]}/documents/${docId[1]}${DOCUMENTS_API}/${privateDocName}`);
            const publicDocument = this.delete(`/projects/${projectId[1]}/documents/${docId[1]}${DOCUMENTS_API}/${publicDocName}`);
            const result = await Promise.allSettled([
                privateDocument,
                publicDocument
            ]);
            result.filter((res)=>res.status === 'rejected').forEach((res)=>logger.log(`couldn't delete collab document and reason ${JSON.stringify(res)}`));
        }), _define_property(this, "getCollaborationSession", async (projectId, docId)=>{
            const privateDocName = getDocumentName(docId, EditorModes.Private);
            const publicDocName = getDocumentName(docId, EditorModes.Public);
            const publicDocSession = await this.get(`/projects/${projectId[1]}/documents/${docId[1]}${DOCUMENTS_API}/${publicDocName}`);
            const privateDocSession = await this.get(`/projects/${projectId[1]}/documents/${docId[1]}${DOCUMENTS_API}/${privateDocName}`);
            return {
                publicDocSession,
                privateDocSession
            };
        }), _define_property(this, "getCollaborationToken", async (clientRegion)=>{
            const params = clientRegion ? {
                clientRegion
            } : undefined;
            return this.get(DOCUMENTS_API, {
                params
            }).then((response)=>{
                logger.debug(`collaborate token api response: ${(response === null || response === void 0 ? void 0 : response.length) > 0 ? 'token available' : 'token not available'}`);
                return response;
            }).catch((error)=>{
                logger.error('get collaborate token api failed', error);
                throw error;
            });
        });
    }
}
export const DocumentsAPI = new DocumentsHttpClient();
